/**
 * UI entry point.
 *
 * Spec §11: this file owns the ONLY timer in the project. It calls step() on an
 * interval and hands snapshots to the renderers and panels. Nothing under
 * src/sim/ ever schedules anything, reads the clock, or touches the DOM.
 *
 * Two loops, deliberately separate:
 *
 *   setInterval(TICK_MS)   advances the simulation and marks the view dirty
 *   requestAnimationFrame  redraws dirty maps, and the drone overlay every frame
 *
 * Keeping them apart is what lets the maps honour spec §12's "redraw on state
 * change only" while drone motion still interpolates smoothly. Merging them
 * would mean repainting 625 city cells at 60 Hz to animate a handful of dots.
 *
 * The layout is a CONSOLE — see console.ts. One screen at a time behind a rail
 * of keys, rather than two maps and an eleven-panel scrolling column.
 */

import '../style.css';

import { TICK_MS } from '../sim/constants';
import { createInitialState } from '../sim/state';
import { step } from '../sim/step';
import type { Command } from '../sim/commands';
import type { GameState } from '../sim/types';
import { drawCityMap } from './canvas/cityMap';
import { drawDroneLayer, drawWorldMap } from './canvas/worldMap';
import { createConsole, type ScreenSpec } from './console';
import { setupInteraction } from './interaction';
import { createAssemblyPanel } from './panels/assemblyPanel';
import { createBuildingPanel } from './panels/buildingPanel';
import { createColonyPanel, createSwitchboardPanel } from './panels/colonyPanel';
import { createDronePanel } from './panels/dronePanel';
import {
  HELP_SECTION_COUNT,
  createHelpPanel,
  createObjectivePanel,
} from './panels/guidePanel';
import { createSessionPanel, createSettingsPanel, type MenuActions } from './panels/menuPanel';
import { createNotificationPanel } from './panels/notificationPanel';
import { createPowerPanel } from './panels/powerPanel';
import { createQueuePanel } from './panels/queuePanel';
import {
  createElementStorePanel,
  createGoodStorePanel,
  createMineralStorePanel,
} from './panels/storesPanel';
import { createSynthesisPanel } from './panels/synthesisPanel';
import { el, setText } from './panels/panel';
import { cycleTheme, currentTheme, initTheme } from './theme';
import { clearSave, loadGame, saveGame, savedInfo } from './persistence';
import { createTitleScreen } from './titleScreen';

/**
 * The world every session is generated from.
 *
 * Overridable from the query string — `?seed=1234` — so playtesters can be
 * handed different worlds, or the same one deliberately. Without it every
 * tester plays the identical planet, which is useful for comparing their
 * reports and useless for finding out how the generator behaves.
 *
 * A seed that is not a finite integer falls back to the default rather than
 * generating a world from NaN.
 */
const DEFAULT_SEED = 20260828;

function seedFromUrl(): number {
  try {
    const raw = new URLSearchParams(window.location.search).get('seed');
    if (raw === null) return DEFAULT_SEED;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : DEFAULT_SEED;
  } catch {
    return DEFAULT_SEED;
  }
}

const SEED = seedFromUrl();

let state: GameState = createInitialState(SEED);
let pending: Command[] = [];
let viewDirty = true;
let lastTickAt = 0;
let paused = false;
/** True while the title screen is up. The world does not advance behind it. */
let titleOpen = true;
/** Set by the menu; shown in the constraint readout until the next tick. */
let notice: string | null = null;

function issue(command: Command): void {
  pending.push(command);
}

interface MapPanel {
  root: HTMLDivElement;
  base: HTMLCanvasElement;
  overlay: HTMLCanvasElement | null;
}

function buildMapPanel(withOverlay: boolean): MapPanel {
  const root = el('div', 'map');
  const base = el('canvas');
  root.append(base);

  let overlay: HTMLCanvasElement | null = null;
  if (withOverlay) {
    overlay = el('canvas', 'overlay');
    root.append(overlay);
  }

  return { root, base, overlay };
}

function buildStat(label: string): { root: HTMLDivElement; value: HTMLSpanElement } {
  const root = el('div', 'stat');
  const value = el('span', 'stat-value');
  root.append(el('span', 'stat-label', label), value);
  return { root, value };
}

const app = document.querySelector<HTMLDivElement>('#app');
if (app === null) throw new Error('#app is missing from index.html');

// --- topbar ---
const topbar = el('div', 'topbar');
const heading = el('h1', undefined, 'Heliocenter');
const tickStat = buildStat('tick');
const creditStat = buildStat('credits');
const popStat = buildStat('pop');
const droneStat = buildStat('drones');
const builtStat = buildStat('built');

/**
 * The constraint readout.
 *
 * The single most useful thing on the console: what the colony is waiting on
 * right now. The simulation already works this out every tick to decide whether
 * the build queue may start its head item; before this it was buried in one
 * panel among eleven, so the answer to "why has nothing been built for ten
 * minutes" required hunting for it.
 */
const statusReadout = el('div', 'status');
const statusLamp = el('span', 'status-lamp');
const statusText = el('span', 'status-text', 'Standing by');
statusReadout.append(statusLamp, statusText);

// Cycles the palette. A button rather than a menu because it is a one-tap
// control on a tablet, and because five themes is a short enough ring to walk.
const themeButton = el('button', 'theme-key');
const themeName = el('span', 'theme-name');
themeButton.append(el('span', 'theme-swatch'), themeName);
themeButton.title = 'Change colour theme';
themeButton.addEventListener('click', () => {
  const theme = cycleTheme();
  setText(themeName, theme.name);
  // The maps take their colours from the palette too, so they must be redrawn.
  viewDirty = true;
});

topbar.append(
  heading,
  tickStat.root,
  creditStat.root,
  popStat.root,
  droneStat.root,
  builtStat.root,
  statusReadout,
  themeButton,
);

setText(themeName, initTheme().name);

// --- maps ---
const worldMapPanel = buildMapPanel(true);
const cityMapPanel = buildMapPanel(false);

// --- panels ---
const buildingPalette = createBuildingPanel();
const interaction = setupInteraction({
  worldCanvas: worldMapPanel.root,
  cityCanvas: cityMapPanel.root,
  getState: () => state,
  activeBuilding: () => buildingPalette.active(),
  issue,
  onChange: () => {
    viewDirty = true;
  },
});

/**
 * A diagnostic a playtester can paste into a message.
 *
 * Deliberately a SUMMARY, not the save. A full GameState is a few hundred
 * kilobytes of planet, which nobody is going to paste anywhere; what a report
 * has to answer is which build, which world, how far it got, and what it was
 * waiting on when it went wrong.
 */
function buildReport(): string {
  const completed = completedPlots(state);
  const phases: Record<string, number> = {};
  for (const drone of state.drones) {
    phases[drone.phase] = (phases[drone.phase] ?? 0) + 1;
  }

  return JSON.stringify(
    {
      build: __BUILD_ID__,
      seed: state.seed,
      tick: state.tick,
      paused,
      plotsComplete: completed,
      queueLength: state.buildQueue.length,
      starved: state.starved,
      power: state.power,
      credits: state.market.credits,
      population: state.colony.population,
      drones: { total: state.drones.length, phases },
      mechs: state.mechs,
      minerals: state.inventory.minerals,
      elements: state.inventory.elements,
      goods: state.inventory.goods,
      screen: shell.activeId(),
      viewport: { width: window.innerWidth, height: window.innerHeight },
      userAgent: navigator.userAgent,
    },
    null,
    2,
  );
}

/**
 * What the session menu can do. Held here rather than in the panel because
 * every one of them touches the loop, the state, or both — the panel is a set
 * of buttons and knows nothing about either.
 */
const menuActions: MenuActions = {
  restart(): void {
    state = createInitialState(SEED);
    pending = [];
    paused = false;
    notice = 'Restarted.';
    viewDirty = true;
  },
  save(): void {
    const outcome = saveGame(state, Date.now());
    notice = outcome.ok ? 'Session saved.' : `Could not save: ${outcome.reason}.`;
    viewDirty = true;
  },
  load(): void {
    const outcome = loadGame();
    if (!outcome.ok) {
      notice = `Could not load: ${outcome.reason}.`;
      viewDirty = true;
      return;
    }
    state = outcome.state;
    pending = [];
    paused = false;
    notice = `Loaded from tick ${state.tick}.`;
    viewDirty = true;
  },
  togglePause(): void {
    paused = !paused;
    viewDirty = true;
  },
  paused: () => paused,
  cycleTheme(): string {
    const theme = cycleTheme();
    setText(themeName, theme.name);
    viewDirty = true;
    return theme.name;
  },
  themeName: () => currentTheme().name,
  savedSummary(): string | null {
    const info = savedInfo();
    if (info === null) return null;
    const minutes = Math.round((Date.now() - info.savedAt) / 60000);
    const when = minutes < 1 ? 'just now' : `${minutes} min ago`;
    return `Saved at tick ${info.tick}, ${when}.`;
  },
  async copyReport(): Promise<string> {
    const report = buildReport();
    try {
      await navigator.clipboard.writeText(report);
      return 'Copied';
    } catch {
      // Clipboard access needs a secure context and a user gesture; if either
      // is missing, say so rather than silently doing nothing.
      notice = 'Could not reach the clipboard. The report is in the browser console.';
      viewDirty = true;
      console.log(report);
      return 'See console';
    }
  },
  exit(): void {
    paused = true;
    clearSave();
    notice = 'Session ended. Restart or load to begin again.';
    viewDirty = true;
  },
};

/**
 * Screens group readings that are consulted together, which is the whole point
 * of the rail: a question the player has should be answerable on one screen.
 *
 *   "where should I send a drone"      → World:   the map and the fleet
 *   "why is nothing being built"       → City:    the map, the palette, the queue
 *   "why are there no components"      → Factory: power, synthesis, stock
 *   "can I bring colonists yet"        → Colony:  housing, food, the switchboard
 */
const screens: ScreenSpec[] = [
  {
    id: 'world',
    label: 'World',
    glyph: '⬡',
    caption: 'Survey the area and dispatch the fleet',
    feature: worldMapPanel.root,
    panels: [createDronePanel(issue, () => interaction.selectedHex())],
  },
  {
    id: 'city',
    label: 'City',
    glyph: '▦',
    caption: 'Lay out the colony and work the build queue',
    feature: cityMapPanel.root,
    panels: [buildingPalette, createQueuePanel(issue)],
  },
  {
    id: 'factory',
    label: 'Factory',
    glyph: '⚙',
    caption: 'Power, refining, synthesis and the assembly bay',
    panels: [createPowerPanel(), createSynthesisPanel(issue), createAssemblyPanel(issue)],
  },
  {
    id: 'colony',
    label: 'Colony',
    glyph: '☖',
    caption: 'Housing, food, and what is switched on',
    panels: [createColonyPanel(issue), createSwitchboardPanel(issue)],
  },
  {
    id: 'stores',
    label: 'Stores',
    glyph: '⇄',
    caption: 'What is held, and what is standing to be traded',
    panels: [
      createMineralStorePanel(),
      createElementStorePanel(issue),
      createGoodStorePanel(issue),
    ],
  },
  {
    id: 'log',
    label: 'Log',
    glyph: '❋',
    caption: 'What the colony has reported',
    panels: [createNotificationPanel()],
  },
  {
    id: 'help',
    label: 'Help',
    glyph: '?',
    caption: 'Controls, and how the colony works',
    panels: [
      createObjectivePanel(),
      ...Array.from({ length: HELP_SECTION_COUNT }, (_, index) => createHelpPanel(index)),
    ],
  },
  {
    id: 'menu',
    label: 'Menu',
    glyph: '⌘',
    caption: 'Save, load, restart, and settings',
    panels: [createSessionPanel(menuActions), createSettingsPanel(menuActions)],
  },
];

const shell = createConsole(screens, () => {
  // A canvas on a hidden screen measures zero, so the map that just became
  // visible has to be measured and drawn again now that it has size.
  viewDirty = true;
});

app.replaceChildren(topbar, shell.root);

/**
 * The title screen, over everything, until the player chooses.
 *
 * Every choice dismisses it — that is what makes it a title screen rather than
 * a panel. New discards whatever was loaded; Continue resumes the save;
 * Settings drops straight into the Menu; Exit halts, and says so, because a
 * page cannot close itself.
 */
const title = createTitleScreen({
  onNew(): void {
    menuActions.restart();
    notice = null;
    shell.show('world');
    titleOpen = false;
  },
  onContinue(): void {
    menuActions.load();
    titleOpen = false;
  },
  onSettings(): void {
    shell.show('menu');
    titleOpen = false;
  },
  onExit(): void {
    titleOpen = false;
    menuActions.exit();
  },
  savedSummary: () => menuActions.savedSummary(),
});
app.append(title.root);

// --- rendering ---
function panelSize(panel: MapPanel): { width: number; height: number } {
  const rect = panel.root.getBoundingClientRect();
  return { width: Math.max(1, rect.width), height: Math.max(1, rect.height) };
}

function completedPlots(current: GameState): number {
  let count = 0;
  for (const row of current.city.grid) {
    for (const plot of row) if (plot !== null && plot.state === 'complete') count += 1;
  }
  return count;
}

/** What the colony is waiting on, in the words the player would use. */
function describeConstraint(current: GameState): { text: string; level: string } {
  const starved = current.starved;
  if (starved !== null) {
    if (starved.reason === 'mechs') {
      return { text: 'Every mech is on a site — build queue waiting', level: 'warn' };
    }
    if (starved.reason === 'disconnected') {
      return { text: 'Next plot is not reachable from the city', level: 'bad' };
    }
    const missing = Object.keys(starved.missing).sort();
    const named = missing.slice(0, 3).join(', ');
    const more = missing.length > 3 ? ` +${missing.length - 3} more` : '';
    return {
      text: named === '' ? 'Build queue short of materials' : `Short of ${named}${more}`,
      level: 'warn',
    };
  }

  if (current.power.throttleBp < 10000) {
    const pct = Math.round(current.power.throttleBp / 100);
    return { text: `Power short — factory at ${pct}%`, level: 'warn' };
  }

  if (current.buildQueue.length > 0) return { text: 'Building', level: 'ok' };
  return { text: 'Queue empty — nothing to build', level: 'idle' };
}

function redraw(): void {
  const active = shell.activeId();

  // Only the map that is actually on screen. The other one has no size.
  if (active === 'world') {
    const world = panelSize(worldMapPanel);
    drawWorldMap(
      worldMapPanel.base,
      state,
      world.width,
      world.height,
      window.devicePixelRatio,
      interaction.selectedHex(),
    );
  } else if (active === 'city') {
    const city = panelSize(cityMapPanel);
    drawCityMap(
      cityMapPanel.base,
      state,
      city.width,
      city.height,
      window.devicePixelRatio,
      interaction.citySelection(),
    );
  }

  setText(tickStat.value, String(state.tick));
  setText(creditStat.value, String(state.market.credits));
  setText(popStat.value, String(state.colony.population));
  setText(droneStat.value, String(state.drones.length));
  setText(builtStat.value, String(completedPlots(state)));

  // A menu notice outranks the constraint until the next tick clears it: the
  // player has just pressed something and wants to know what happened.
  if (notice !== null) {
    setText(statusText, notice);
    statusReadout.dataset['level'] = 'ok';
  } else if (paused) {
    setText(statusText, 'Paused');
    statusReadout.dataset['level'] = 'idle';
  } else {
    const constraint = describeConstraint(state);
    setText(statusText, constraint.text);
    statusReadout.dataset['level'] = constraint.level;
  }

  shell.update(state);
}

function frame(now: number): void {
  if (viewDirty) {
    redraw();
    viewDirty = false;
  }

  if (worldMapPanel.overlay !== null && shell.activeId() === 'world') {
    // Fraction of the current tick already elapsed. Cosmetic only — nothing
    // computed here is ever read back by the simulation.
    const alpha = lastTickAt === 0 ? 0 : Math.min(1, (now - lastTickAt) / TICK_MS);
    const world = panelSize(worldMapPanel);
    drawDroneLayer(
      worldMapPanel.overlay,
      state,
      alpha,
      world.width,
      world.height,
      window.devicePixelRatio,
    );
  }

  window.requestAnimationFrame(frame);
}

function tick(): void {
  // Reading the introduction should not cost the player ticks of their colony.
  if (titleOpen) return;

  if (paused) {
    // Commands still apply while paused, so a player can set up an order and
    // then let it run; the world simply does not advance.
    if (pending.length > 0) {
      state = step(state, pending);
      pending = [];
      viewDirty = true;
    }
    return;
  }

  notice = null;
  state = step(state, pending);
  pending = [];
  lastTickAt = performance.now();
  viewDirty = true;
}

new ResizeObserver(() => {
  viewDirty = true;
}).observe(shell.root);

window.setInterval(tick, TICK_MS);
window.requestAnimationFrame(frame);
